import React from 'react';
import { Facebook, Linkedin, Instagram, Phone, Mail, MapPin, Youtube, MessageCircle, ShieldCheck } from 'lucide-react';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Twitter/X icon component
const TwitterXIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Spotify icon component
const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0m3.669 11.538a.5.5 0 0 1-.686.165c-1.879-1.147-4.243-1.407-7.028-.77a.499.499 0 0 1-.222-.973c3.048-.696 5.662-.397 7.77.892a.5.5 0 0 1 .166.686m.979-2.178a.624.624 0 0 1-.858.205c-2.15-1.321-5.428-1.704-7.972-.932a.625.625 0 0 1-.362-1.194c2.905-.881 6.517-.454 8.986 1.063a.624.624 0 0 1 .206.858m.084-2.268C10.154 5.56 5.9 5.419 3.438 6.166a.748.748 0 1 1-.434-1.432c2.825-.857 7.523-.692 10.492 1.07a.747.747 0 1 1-.764 1.288"/>
  </svg>
);

export default function Footer({ onNavigate }) {
  const font = { fontFamily: "'Outfit', sans-serif" };

  // Link columns. Order follows the menu.
  const columns = [
    {
      title: 'Who it is for',
      links: [
        { name: 'Farmers', page: 'farmers' },
        { name: 'Farmer Groups', page: 'groups' },
        { name: 'Cooperatives', page: 'cooperatives' },
        { name: 'Insurers', page: 'insurers' },
        { name: 'Lenders', page: 'lenders' },
      ],
    },
    {
      title: 'Partners',
      links: [
        { name: 'Veterinary offices', page: 'vets' },
        { name: 'Buyers and processors', page: 'buyers' },
        { name: 'Governments and universities', page: 'governments' },
      ],
    },
    {
      title: 'Imfuyo',
      links: [
        { name: 'Home', page: 'landing' },
        { name: 'About', page: 'about' },
        { name: 'What We Do', page: 'whatwedo' },
        { name: 'Market Place', page: 'shop' },
        { name: 'Blog & Community', page: 'community' },
        { name: 'My Imfuyo account', page: 'account' },
      ],
    },
  ];

  const socialLinks = [
    { 
      icon: Facebook, 
      label: 'Facebook', 
      href: 'https://www.facebook.com/ImfuyoKenya',
      color: '#1877F2'
    },
    { 
      icon: TwitterXIcon, 
      label: 'X (Twitter)', 
      href: 'https://x.com/Imfuyousajili',
      color: '#000000'
    },
    { 
      icon: TikTokIcon, 
      label: 'TikTok', 
      href: 'https://www.tiktok.com/@imfuyo.kenya',
      color: '#000000'
    },
    { 
      icon: Linkedin, 
      label: 'LinkedIn', 
      href: 'https://www.linkedin.com/company/imfuyo-usajili',
      color: '#0A66C2'
    },
    { 
      icon: Instagram, 
      label: 'Instagram', 
      href: 'https://www.instagram.com/imfuyo.farm/',
      color: '#E4405F'
    },
    { 
      icon: SpotifyIcon, 
      label: 'Spotify', 
      href: 'https://open.spotify.com/show/0tBRxfkKYvil2tWiiEtq2i?si=ad9a82213a864f8e',
      color: '#1DB954'
    },
    { 
      icon: Youtube, 
      label: 'YouTube', 
      href: 'https://www.youtube.com/@Imfuyo',
      color: '#FF0000'
    },
    { 
      icon: MessageCircle, 
      label: 'WhatsApp', 
      href: 'https://wa.me/254110563185',
      color: '#25D366'
    },
    { 
      icon: Linkedin, 
      label: 'Imfuyo Foundation', 
      href: 'https://www.linkedin.com/showcase/imfuyo-foundation/?viewAsMember=true',
      color: '#0A66C2'
    }
  ];

  const go = (page) => {
    if (onNavigate) onNavigate(page);
    else { sessionStorage.setItem('targetPage', page); window.location.reload(); }
  };

  // Email addresses are assembled on click so they are not printed in the page source as plain links.
  const mail = (name) => () => { window.location.href = `mailto:${name}@imfuyo.farm`; };

  const linkClass = 'text-[15px] text-white/75 hover:text-white transition-colors text-left';

  return (
    <footer className="w-full bg-[#14301A] text-white" style={font}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">

        {/* Main grid: brand + three link columns + contact */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-4">
            <button onClick={() => go('landing')} className="flex items-center gap-3 mb-4" aria-label="Imfuyo home">
              <img
                src="https://res.cloudinary.com/dpymwa41m/image/upload/v1761939036/Gemini_Generated_Image_b62mhwb62mhwb62m_s1dmj2.png"
                alt="Imfuyo"
                className="w-12 h-12 rounded-lg"
              />
              <span className="text-2xl font-bold">Imfuyo</span>
            </button>
            <p className="text-white/75 text-[15px] leading-relaxed max-w-sm">
              Imfuyo is a data and finance company in the livestock value chain.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {socialLinks.map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  title={social.label}
                  className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <social.icon className="w-[18px] h-[18px] text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <nav key={col.title} className="lg:col-span-2" aria-label={col.title}>
              <h3 className="text-sm font-semibold tracking-widest uppercase text-[#B7D9A8] mb-4">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.name}>
                    <button onClick={() => go(l.page)} className={linkClass}>{l.name}</button>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Contact */}
          <div className="col-span-2 lg:col-span-2">
            <h3 className="text-sm font-semibold tracking-widest uppercase text-[#B7D9A8] mb-4">Contact</h3>
            <ul className="space-y-3 text-[15px] text-white/75">
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 mt-1 shrink-0" />
                <div className="flex flex-col items-start gap-1">
                  <button onClick={mail('hello')} className="hover:text-white text-left">hello@imfuyo.farm</button>
                  <button onClick={mail('foundation')} className="hover:text-white text-left">foundation@imfuyo.farm</button>
                </div>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 shrink-0" />
                <a href="tel:+254110563185" className="hover:text-white">+254 110 563 185</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-1 shrink-0" />
                <div>
                  <div>Nairobi, Kenya</div>
                  <div>Chatham, New Jersey</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Data protection registration: one slim row */}
        <div className="mt-12 rounded-2xl border border-white/15 bg-white/5 p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <ShieldCheck className="w-8 h-8 text-[#FBBF24] shrink-0" />
            <div>
              <h3 className="font-semibold">Officially Registered with the ODPC</h3>
              <p className="text-sm text-white/70 leading-relaxed mt-1 max-w-3xl">
                Imfuyo is registered with Kenya's Office of the Data Protection Commissioner (ODPC).
                Any personal information you share with us — as a farmer, partner, or visitor — is
                handled carefully, stored securely, and used only for the purpose that you gave it to us for.
              </p>
            </div>
          </div>
          <div className="shrink-0 rounded-xl bg-white/10 px-4 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#FBBF24]">Data Protection Certificate No.</div>
            <div className="font-mono font-bold tracking-wide">151-193C-B376</div>
          </div>
        </div>

        {/* Legal */}
        <div className="mt-8 pt-6 border-t border-white/15 flex flex-col md:flex-row md:items-start md:justify-between gap-2 text-sm text-white/60">
          <p>© {new Date().getFullYear()} Imfuyo Usajili Solutions Limited. All rights reserved. <a href="/privacy" onClick={(e) => { e.preventDefault(); go('privacy'); }} className="underline underline-offset-2 hover:text-white">Privacy and cookies</a></p>
          <p className="md:text-right max-w-xl">
            Imfuyo Foundation is a separate non-profit, a 501(c)(3) incorporated in New Jersey.
          </p>
        </div>
      </div>
    </footer>
  );
}
